// FILE: src/components/case-studies/admin/CaseStudiesScreen.js
//
// Admin case-study list (Case Study Question Framework, PR 2). Flag-gated by
// caseStudyAdminEnabled; reached via the AdminQuickActions ribbon.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminImportCaseStudy, adminListCaseStudies, adminUpsertCaseStudy } from '../../../services/caseStudyService';
import { getActiveCertIds } from '../../../config/exams';
import CaseStudyEditor from './CaseStudyEditor';
import ImportCaseModal from './ImportCaseModal';
import { S, T, statusChip } from './caseAdminStyles';

const STATUSES = ['draft', 'review', 'approved', 'published', 'archived'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard', 'Expert'];
const APPROACHES = ['predictive', 'agile', 'hybrid', 'mixed'];

export default function CaseStudiesScreen({ onBack, onEditQuestion, initialOpenId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ certification: '', status: '', difficulty: '', delivery_approach: '' });
  const [openId, setOpenId] = useState(initialOpenId ?? null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [draft, setDraft] = useState({ title: '', slug: '', certification: 'pmp' });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setRows(await adminListCaseStudies(null));
    } catch (e) { setError(e.message); setRows([]); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => (rows || []).filter((r) =>
    (!filters.certification || r.certification === filters.certification) &&
    (!filters.status || r.status === filters.status) &&
    (!filters.difficulty || r.difficulty === filters.difficulty) &&
    (!filters.delivery_approach || r.delivery_approach === filters.delivery_approach)
  ), [rows, filters]);

  async function createCase() {
    if (!draft.title.trim() || !draft.slug.trim()) { setError('Title and slug are required.'); return; }
    setBusy(true);
    try {
      const row = await adminUpsertCaseStudy({ ...draft, slug: draft.slug.trim() });
      setCreating(false); setDraft({ title: '', slug: '', certification: 'pmp' });
      await reload(); setOpenId(row.id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  if (openId) {
    return <CaseStudyEditor caseStudyId={openId} onBack={() => { setOpenId(null); reload(); }} onEditQuestion={onEditQuestion} />;
  }

  const sel = (key, options, label) => (
    <label style={{ fontSize: '0.78rem', color: T.muted }}>
      {label}{' '}
      <select data-testid={`case-filter-${key}`} style={{ ...S.input, width: 'auto', display: 'inline-block' }}
        value={filters[key]} onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );

  return (
    <div style={S.page} data-testid="case-studies-screen">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button style={S.btn} data-testid="case-studies-back" onClick={onBack}>← Back</button>
        <div>
          <h1 style={S.h1}>📚 Case Studies</h1>
          <div style={S.sub}>Author stage-gated case studies. Learner flow ships in a later phase.</div>
        </div>
      </div>

      {error && <div style={S.err} data-testid="case-list-error">{error}</div>}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {sel('certification', getActiveCertIds(), 'Certification')}
        {sel('status', STATUSES, 'Status')}
        {sel('difficulty', DIFFICULTIES, 'Difficulty')}
        {sel('delivery_approach', APPROACHES, 'Approach')}
        <span style={{ flex: 1 }} />
        <button style={S.btn} data-testid="case-import-button" onClick={() => setImporting(true)}>⇪ Import case</button>
        <button style={S.btnPrimary} data-testid="case-create-button" onClick={() => setCreating(true)}>+ New case study</button>
      </div>

      <div style={{ ...S.panel, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
          <thead>
            <tr style={{ background: T.bg, textAlign: 'left' }}>
              {['Title', 'Cert', 'Approach', 'Difficulty', 'Status', 'Questions', 'Artifacts', 'Tags', 'Updated', 'Version'].map((h) => (
                <th key={h} style={{ padding: '9px 12px', borderBottom: `1px solid ${T.line}`, color: T.muted, fontSize: '0.72rem', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="case-list-body">
            {rows === null && <tr><td colSpan={10} style={{ padding: 16, color: T.muted }}>Loading…</td></tr>}
            {rows !== null && filtered.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 16, color: T.muted }}>
                No case studies match.{' '}
                <button style={{ ...S.btnGhost, color: T.brand, fontWeight: 700 }}
                  data-testid="case-create-empty-cta" onClick={() => setCreating(true)}>
                  + Create the first one
                </button>
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`case-row-${r.slug}`} style={{ cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}`, fontWeight: 700 }}>{r.title}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}>{(r.certification || '').toUpperCase()}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}`, textTransform: 'capitalize' }}>{r.delivery_approach || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}>{r.difficulty || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}><span style={statusChip(r.status)}>{r.status}</span></td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}>{r.question_count}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}>{r.artifact_count}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}`, color: T.muted, fontSize: '0.76rem' }}>{(r.tags || []).join(', ') || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}`, color: T.muted }}>{(r.updated_at || '').slice(0, 10)}</td>
                <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.line}` }}>v{r.version}{r.published_at ? '' : ' (unpublished)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importing && (
        <ImportCaseModal
          onClose={() => setImporting(false)}
          onImported={(caseId) => { setImporting(false); reload(); if (caseId) setOpenId(caseId); }}
          importFn={adminImportCaseStudy}
        />
      )}

      {creating && (
        <div style={S.modalScrim} onClick={() => !busy && setCreating(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()} data-testid="case-create-modal">
            <h2 style={{ ...S.h1, marginBottom: 10 }}>New case study</h2>
            <label style={S.label}>Title</label>
            <input style={S.input} data-testid="case-create-title" value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <label style={S.label}>Slug (URL-safe identifier)</label>
            <input style={S.input} data-testid="case-create-slug" value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
            <label style={S.label}>Certification</label>
            <select style={S.input} value={draft.certification}
              onChange={(e) => setDraft({ ...draft, certification: e.target.value })}>
              {getActiveCertIds().map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button style={S.btn} disabled={busy} onClick={() => setCreating(false)}>Cancel</button>
              <button style={S.btnPrimary} data-testid="case-create-submit" disabled={busy} onClick={createCase}>
                {busy ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
